
import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop, Watch } from 'vue-property-decorator';
import Datim from '@/components/shared/Datim.vue';
import SimpleConfirmationDialog from '@/components/shared/SimpleConfirmationDialog.vue';
import { collectErrors, fieldErrorMessages, generalErrorMessage } from '@/components/settings/identityIntegrations/integrationErrors';
import {
  IApiFieldError,
  identityIntegrationsService,
  IIdentityIntegration,
  IIdentityIntegrationCertificateSummary,
} from '@/services/identityIntegrationsService';

const MAPPED_FIELDS = ['idp_certificate'];

@Component({
  name: 'CertificateCard',
  components: { Datim, SimpleConfirmationDialog },
})
export default class CertificateCard extends Vue {
  @Prop({ required: true })
  protected integration!: IIdentityIntegration;
  @Prop({ default: false })
  protected canManage!: boolean;

  protected input: string = '';
  protected busy: boolean = false;
  protected errors: IApiFieldError[] = [];
  protected confirmDialog: boolean = false;
  protected replacing: boolean = false;

  readonly placeholder: string = '-----BEGIN CERTIFICATE-----\nMIID...\n-----END CERTIFICATE-----';

  @Watch('integration')
  onIntegrationChanged() {
    this.errors = [];
  }

  get certificate(): IIdentityIntegrationCertificateSummary {
    return this.integration.certificate;
  }

  // Only a certificate that works can be tucked away; anything else is the
  // problem on screen.
  get canCollapse(): boolean {
    return this.certificate.configured && this.certificate.usable;
  }

  get editorOpen(): boolean {
    return !this.canCollapse || this.replacing;
  }

  get hasInput(): boolean {
    return this.input.trim() !== '';
  }

  get isExpired(): boolean {
    if (!this.certificate.valid_to) {
      return false;
    }

    return new Date(this.certificate.valid_to).getTime() < Date.now();
  }

  get stateLabel(): string {
    if (!this.certificate.configured) {
      return this.$t('identityIntegrations.certificate.missing') as string;
    }
    if (!this.certificate.usable) {
      return this.$t('identityIntegrations.certificate.invalid') as string;
    }
    return this.$t('identityIntegrations.certificate.installed') as string;
  }

  get stateClass(): string {
    if (!this.certificate.configured) {
      return 'ii-cert-state--warn';
    }
    return this.certificate.usable ? 'ii-cert-state--ok' : 'ii-cert-state--error';
  }

  get generalError(): string | null {
    return generalErrorMessage(this, this.errors, MAPPED_FIELDS);
  }

  fieldError(field: string): string[] {
    return fieldErrorMessages(this, this.errors, field);
  }

  closeEditor() {
    this.replacing = false;
    this.input = '';
    this.errors = [];
  }

  // The confirmation is about what stops working. Installing the first
  // certificate stops nothing, so it does not ask.
  requestSubmit() {
    if (this.certificate.configured) {
      this.confirmDialog = true;
    } else {
      this.submit();
    }
  }

  async submit() {
    this.confirmDialog = false;

    if (!this.hasInput) {
      return;
    }

    this.busy = true;
    this.errors = [];
    try {
      const updated = await identityIntegrationsService.replaceCertificate(this.integration.id, this.input.trim());
      this.input = '';
      this.replacing = false;
      this.$uiBus.emit('info', this.$t('identityIntegrations.certificate.replaced') as string);
      this.$emit('updated', updated);
    } catch (error) {
      this.errors = collectErrors(error);
    } finally {
      this.busy = false;
    }
  }
}
